import { Link } from "react-router-dom";
import { LuCheck as Check, LuSparkles as Sparkles } from "react-icons/lu";

export default function PricingPage() {
    const plans = [
        {
            name: "Free",
            price: "€0",
            period: "forever",
            description: "Perfect for testing and exploring ideas",
            features: [
                "3 free tries - no signup required",
                "10 name generations per day (with account)",
                "Full BRAND FIT scoring (8 dimensions)",
                "Domain availability check (.com)",
                "Save unlimited favorites (with account)",
                "Save unlimited sessions (with account)",
                "Access from any device"
            ],
            limitations: [
                "Daily generation limit (resets every 24h)",
                "Single domain check per name"
            ],
            cta: "Get Started",
            highlighted: false,
            available: true
        },
        {
            name: "Pro",
            price: "Coming soon",
            period: "",
            description: "For serious founders building their brand",
            features: [
                "Unlimited name generations",
                "Full BRAND FIT scoring (8 dimensions)",
                "Multi-TLD domain checks (.com, .io, .ai, .co, .app, .net, .org)",
                "Unlimited favorites and saved sessions",
                "Compare names side-by-side with AI analysis",
                "Generate marketing slogans for names",
                "Export results to CSV",
                "Session sharing via shareable links",
                "Advanced filtering and sorting",
                "Priority email support"
            ],
            limitations: [],
            cta: "Join Waitlist",
            highlighted: true,
            available: false,
            badge: "Coming Soon"
        },
        {
            name: "Enterprise",
            price: "Contact us",
            period: "",
            description: "For teams and agencies managing multiple brands",
            features: [
                "Everything in Pro",
                "USPTO trademark search integration",
                "EU & International trademark databases",
                "Team collaboration & workspace management",
                "API access for integrations",
                "Custom brand guidelines and scoring criteria",
                "White-label options",
                "Dedicated account manager",
                "SLA guarantees"
            ],
            limitations: [],
            cta: "Contact Sales",
            highlighted: false,
            available: false,
            badge: "Coming Soon"
        }
    ];

    return (
        <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4 text-gray-900">
                    Simple, Transparent Pricing
                </h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Start free, upgrade when you need more power. No hidden fees, cancel anytime.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
                {plans.map((plan) => (
                    <div 
                        key={plan.name} 
                        className={`relative rounded-2xl p-8 flex flex-col ${
                            plan.highlighted 
                                ? 'bg-gradient-to-b from-indigo-50 to-white border-2 border-indigo-600 shadow-xl' 
                                : 'bg-white border border-gray-200 shadow-sm'
                        }`}
                    >
                        {/* Badge */}
                        {plan.badge && (
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                <span className={`text-xs font-bold px-4 py-1.5 rounded-full ${
                                    plan.highlighted 
                                        ? 'bg-indigo-600 text-white' 
                                        : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {plan.badge}
                                </span>
                            </div>
                        )}

                        {/* Plan Header */}
                        <div className="mb-6">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                            <div className="flex items-baseline gap-2 mb-3">
                                <span className="text-5xl font-extrabold text-gray-900">{plan.price}</span>
                                {plan.period && (
                                    <span className="text-gray-600">/ {plan.period}</span>
                                )}
                            </div>
                            <p className="text-gray-600">{plan.description}</p>
                        </div>

                        {/* Features List */}
                        <ul className="space-y-3 mb-8 flex-1">
                            {plan.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                        plan.highlighted ? 'text-indigo-600' : 'text-green-600'
                                    }`} />
                                    <span className="text-sm text-gray-700">{feature}</span>
                                </li>
                            ))}
                        </ul>

                        {/* CTA Button */}
                        {plan.available ? (
                            <Link 
                                to="/app" 
                                className={`block text-center px-6 py-3 rounded-lg font-semibold transition ${
                                    plan.highlighted
                                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg'
                                        : 'bg-gray-900 text-white hover:bg-gray-800'
                                }`}
                            >
                                {plan.cta}
                            </Link>
                        ) : (
                            <button 
                                disabled
                                className="block w-full text-center px-6 py-3 rounded-lg font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                            >
                                {plan.cta}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* FAQ or Additional Info */}
            <div className="bg-indigo-50 rounded-2xl p-8 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                    <h3 className="text-2xl font-bold text-gray-900">All plans include</h3>
                </div>
                <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-6">
                    <div className="text-sm">
                        <div className="font-semibold text-gray-900 mb-1">Try Before Signup</div>
                        <div className="text-gray-600">3 free tries, no account needed</div>
                    </div>
                    <div className="text-sm">
                        <div className="font-semibold text-gray-900 mb-1">No Credit Card</div>
                        <div className="text-gray-600">Start free, no strings</div>
                    </div>
                    <div className="text-sm">
                        <div className="font-semibold text-gray-900 mb-1">Cancel Anytime</div>
                        <div className="text-gray-600">No long-term commitment</div>
                    </div>
                    <div className="text-sm">
                        <div className="font-semibold text-gray-900 mb-1">Lightning Fast</div>
                        <div className="text-gray-600">Results in seconds</div>
                    </div>
                </div>
                <div className="border-t border-indigo-200 pt-6">
                    <p className="text-sm text-gray-700 max-w-2xl mx-auto">
                        <strong>New:</strong> Anonymous users can try the generator with 3 free credits—no signup required. 
                        Sign up to get 10 generations per day, or upgrade to Pro for unlimited access.
                    </p>
                </div>
            </div>
        </div>
    );
}
